import type { SimStep } from "./types";

/* ──────────────────────────────────────────────────────────────────────────
 * Worst-stretch detector — find the longest continuous span where the
 * portfolio dipped below the loan balance, plus the maximum dollar gap
 * during that stretch.
 *
 * Used by the `/simulate` outcome supporting line:
 *   "Worst stretch — $X underwater for N months in [year]."
 *
 * Pure function. Returns null when the portfolio never went underwater
 * across the entire window — in that case the page shows "stayed above"
 * instead.
 *
 * Semantics (per Slice 11 spec):
 *   • Underwater = portfolio < loan at a given step
 *   • Worst stretch = the LONGEST continuous span where this was true
 *     (not the deepest single moment — ties broken by deeper depth)
 *   • Depth = max (loan − portfolio) during that stretch
 *   • Duration = months between stretch start and end (inclusive)
 *   • Year = calendar year containing the deepest point (the emotional
 *     anchor of the stretch)
 * ────────────────────────────────────────────────────────────────────────── */

export interface WorstStretch {
  /** ISO date the underwater span started. */
  startDate: string;
  /** ISO date the underwater span ended (last step still underwater). */
  endDate: string;
  /** Maximum (loan − portfolio) gap observed during the stretch, in dollars. */
  depthDollars: number;
  /** Months from start to end, inclusive. Same calendar month counts as 1. */
  durationMonths: number;
  /** Calendar year of the deepest point. */
  yearOfDeepest: number;
}

export function findWorstStretch(steps: readonly SimStep[]): WorstStretch | null {
  if (steps.length === 0) return null;

  interface Candidate {
    startIndex: number;
    endIndex: number;
    depthDollars: number;
    indexOfDeepest: number;
  }

  let best: Candidate | null = null;
  let current: Candidate | null = null;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    const gap = step.loanBalance - step.portfolioValue;
    const underwater = gap > 0;

    if (underwater) {
      if (current === null) {
        current = { startIndex: i, endIndex: i, depthDollars: gap, indexOfDeepest: i };
      } else {
        current.endIndex = i;
        if (gap > current.depthDollars) {
          current.depthDollars = gap;
          current.indexOfDeepest = i;
        }
      }
    } else if (current !== null) {
      best = pickLonger(best, current, steps);
      current = null;
    }
  }
  if (current !== null) {
    best = pickLonger(best, current, steps);
  }

  if (!best) return null;

  return {
    startDate: steps[best.startIndex]!.date,
    endDate: steps[best.endIndex]!.date,
    depthDollars: best.depthDollars,
    durationMonths: monthsBetweenInclusive(
      steps[best.startIndex]!.date,
      steps[best.endIndex]!.date,
    ),
    yearOfDeepest: Number(steps[best.indexOfDeepest]!.date.slice(0, 4)),
  };
}

/** Pick the longer stretch by step-count; tie-break by deeper depth. */
function pickLonger(
  current: {
    startIndex: number;
    endIndex: number;
    depthDollars: number;
    indexOfDeepest: number;
  } | null,
  candidate: { startIndex: number; endIndex: number; depthDollars: number; indexOfDeepest: number },
  _steps: readonly SimStep[],
): typeof candidate {
  if (!current) return candidate;
  const currentLen = current.endIndex - current.startIndex;
  const candidateLen = candidate.endIndex - candidate.startIndex;
  if (candidateLen > currentLen) return candidate;
  if (candidateLen < currentLen) return current;
  return candidate.depthDollars > current.depthDollars ? candidate : current;
}

/**
 * Months from start to end, inclusive of both endpoints' calendar months.
 * Two dates in the same calendar month count as 1.
 */
function monthsBetweenInclusive(startIso: string, endIso: string): number {
  const [sy, sm] = startIso.split("-").map(Number);
  const [ey, em] = endIso.split("-").map(Number);
  if (sy === undefined || sm === undefined || ey === undefined || em === undefined) return 0;
  return (ey - sy) * 12 + (em - sm) + 1;
}
